import React, {
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import {
  Document,
  Page,
} from 'react-pdf';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import isNil from 'lodash/isNil';
import {
  notEmptyString,
} from '@populateui/utils';
import Button from 'pages/Dashboard/components/Button';
import IconButton from 'core/components/IconButton';
import DownloadIcon from 'core/icons/download';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  downloadFileFromUrl,
} from 'pages/Dashboard/utils/helper';

const zoomStep = 0.1;
const minimumZoom = 0.5;
const maximumZoom = 2;
const defaultDownloadFileName = 'document.pdf';
const pageRenderBuffer = 1;
const defaultPageAspectRatio = 11 / 8.5;

type Props = {
  fileUrl: string;
  fileName?: string;
  thumbnail?: boolean;
  className?: string;
  canDownload?: boolean;
};

type PdfMediaToolbarProps = {
  fileUrl: string;
  fileName?: string;
  canDownload: boolean;
  zoom: number;
  numPages: number;
  currentPage: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitWidth: () => void;
  onOpen: () => void;
};

const toolbarIconButtonClassName = clsx(
  '!h-8 !w-8 !border !border-gray-200 !bg-white !text-gray-700 hover:!bg-gray-50',
);

function PdfMediaToolbar({
  fileUrl,
  fileName,
  canDownload,
  zoom,
  numPages,
  currentPage,
  onZoomIn,
  onZoomOut,
  onFitWidth,
  onOpen,
}: PdfMediaToolbarProps) {
  const pageLabel = numPages > 0
    ? `${currentPage} / ${numPages}`
    : '— / —';

  return (
    <div
      className={clsx(
        'flex shrink-0 items-center gap-2 border-b border-gray-200',
        'bg-gray-50 px-2 py-1.5',
      )}
      data-testid="pdf-media-toolbar"
      role="toolbar"
      aria-label="PDF preview controls"
      tabIndex={-1}
      onClick={(event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
      }}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
        event.stopPropagation();
      }}
    >
      <div className="flex flex-1 items-center gap-1">
        <IconButton
          aria-label="Zoom out"
          tooltip="Zoom out"
          className={toolbarIconButtonClassName}
          onClick={(event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            onZoomOut();
          }}
        >
          <RemoveRoundedIcon className="!h-4 !w-4" />
        </IconButton>
        <span className="w-12 text-center text-xs font-semibold text-gray-600">
          {Math.round(zoom * 100)}
          %
        </span>
        <IconButton
          aria-label="Zoom in"
          tooltip="Zoom in"
          className={toolbarIconButtonClassName}
          onClick={(event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            onZoomIn();
          }}
        >
          <AddRoundedIcon className="!h-4 !w-4" />
        </IconButton>
        <IconButton
          aria-label="Fit width"
          tooltip="Fit width"
          className={toolbarIconButtonClassName}
          onClick={(event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            onFitWidth();
          }}
        >
          <FitScreenIcon className="!h-4 !w-4" />
        </IconButton>
      </div>
      <span className="text-xs font-semibold text-gray-600" data-testid="pdf-page-indicator">
        {pageLabel}
      </span>
      <div className="flex flex-1 items-center justify-end gap-1">
        {notEmptyString(fileUrl) && (
          <IconButton
            aria-label="Open in new tab"
            tooltip="Open in new tab"
            className={toolbarIconButtonClassName}
            onClick={(event: MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              onOpen();
            }}
          >
            <OpenInNewIcon className="!h-4 !w-4" />
          </IconButton>
        )}
        {canDownload && notEmptyString(fileUrl) && (
          <IconButton
            aria-label="Download"
            tooltip="Download"
            className={toolbarIconButtonClassName}
            onClick={async (event: MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              await downloadFileFromUrl(fileUrl, fileName ?? defaultDownloadFileName);
            }}
          >
            <DownloadIcon className="!h-4 !w-4" />
          </IconButton>
        )}
      </div>
    </div>
  );
}

export default function PdfMediaPreview({
  fileUrl,
  fileName,
  thumbnail = false,
  className,
  canDownload = false,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasMeasuredPageAspectRatioRef = useRef(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageAspectRatio, setPageAspectRatio] = useState(defaultPageAspectRatio);

  useEffect(() => {
    setZoom(1);
    setCurrentPage(1);
    setNumPages(0);
    setHasError(false);
    setPageAspectRatio(defaultPageAspectRatio);
    hasMeasuredPageAspectRatioRef.current = false;
  }, [fileUrl]);

  useEffect(() => {
    const element = containerRef.current;
    if (isNil(element)) {
      return undefined;
    }
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      setContainerWidth(Math.floor(width));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const root = containerRef.current;
    if (isNil(root) || hasError || numPages < 1) {
      return undefined;
    }

    const pageElements = root.querySelectorAll<HTMLElement>('[data-page]');
    if (pageElements.length === 0) {
      return undefined;
    }

    const visibilityByPage = new Map<number, number>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const pageValue = Number(entry.target.getAttribute('data-page'));
          if (Number.isNaN(pageValue)) {
            return;
          }
          visibilityByPage.set(pageValue, entry.isIntersecting ? entry.intersectionRatio : 0);
        });

        let bestPage = 1;
        let bestRatio = -1;
        visibilityByPage.forEach((ratio, pageNumber) => {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestPage = pageNumber;
          }
        });

        if (bestRatio > 0) {
          setCurrentPage(Math.min(Math.max(bestPage, 1), numPages));
        }
      },
      {
        root,
        threshold: [0.25, 0.5, 0.75],
      },
    );

    pageElements.forEach((pageElement) => {
      observer.observe(pageElement);
    });

    return () => observer.disconnect();
  }, [hasError, numPages, zoom, containerWidth]);

  const pageNumbers = Array.from(
    { length: numPages },
    (_, index) => index + 1,
  );
  const pageWidth = Math.floor(containerWidth * zoom);
  const estimatedPageHeight = pageWidth > 0
    ? Math.floor(pageWidth * pageAspectRatio)
    : 0;
  const showToolbar = !thumbnail && !hasError;

  return (
    <div className={clsx('flex h-full w-full flex-col', className)}>
      {showToolbar && (
        <PdfMediaToolbar
          fileUrl={fileUrl}
          fileName={fileName}
          canDownload={canDownload}
          zoom={zoom}
          numPages={numPages}
          currentPage={currentPage}
          onZoomIn={() => {
            setZoom((previousZoom) => Math.min(maximumZoom, previousZoom + zoomStep));
          }}
          onZoomOut={() => {
            setZoom((previousZoom) => Math.max(minimumZoom, previousZoom - zoomStep));
          }}
          onFitWidth={() => {
            setZoom(1);
          }}
          onOpen={() => {
            if (notEmptyString(fileUrl)) {
              window.open(fileUrl, '_blank', 'noopener,noreferrer');
            }
          }}
        />
      )}
      <div
        ref={containerRef}
        className="min-h-0 flex-1 overflow-y-auto"
      >
        {hasError ? (
          <div className="flex h-full w-full flex-col items-center justify-center gap-3 p-4 text-center">
            <p className="text-sm text-gray-500">
              Preview is not available for this document.
            </p>
            {canDownload && notEmptyString(fileUrl) && (
              <Button
                variant="contained"
                color="primary"
                onClick={async () => {
                  await downloadFileFromUrl(fileUrl, fileName ?? defaultDownloadFileName);
                }}
              >
                <DownloadIcon className="mr-2 !w-4 !h-4 text-white" />
                Download
              </Button>
            )}
          </div>
        ) : (
          <Document
            file={notEmptyString(fileUrl) ? fileUrl : undefined}
            loading={(
              <div className="flex h-full w-full items-center justify-center">
                <Spinner size={32} />
              </div>
            )}
            onLoadSuccess={({ numPages: loadedPageCount }) => {
              setHasError(false);
              setNumPages(loadedPageCount);
              setCurrentPage(1);
            }}
            onLoadError={() => setHasError(true)}
            onSourceError={() => setHasError(true)}
          >
            {pageNumbers.map((pageNumber) => {
              const shouldRenderPage = Math.abs(pageNumber - currentPage) <= pageRenderBuffer;

              return (
                <div
                  key={pageNumber}
                  className="mb-2 flex justify-center"
                  data-page={pageNumber}
                  style={estimatedPageHeight > 0 ? { minHeight: estimatedPageHeight } : undefined}
                >
                  {pageWidth > 0 && shouldRenderPage ? (
                    <Page
                      pageNumber={pageNumber}
                      width={pageWidth}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      onLoadSuccess={(page) => {
                        if (hasMeasuredPageAspectRatioRef.current) {
                          return;
                        }
                        const viewport = page.getViewport({ scale: 1 });
                        if (viewport.width <= 0) {
                          return;
                        }
                        hasMeasuredPageAspectRatioRef.current = true;
                        setPageAspectRatio(viewport.height / viewport.width);
                      }}
                    />
                  ) : null}
                </div>
              );
            })}
          </Document>
        )}
      </div>
    </div>
  );
}
